<template>
  <div class="dashboard-reliability">
    <div class="rel-header">
      <h3 class="rel-title">{{ $t('org-dashboard.reliability-title') }}</h3>
      <div class="rel-subtitle">{{ $t('org-dashboard.reliability-subtitle') }}</div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="rel-loading">
      <v-skeleton-loader type="card" />
    </div>

    <!-- Empty -->
    <div v-else-if="!data" class="rel-empty">
      <v-icon color="grey lighten-1" size="40">mdi-shield-check</v-icon>
      <p>{{ $t('org-dashboard.peak-no-data') }}</p>
    </div>

    <template v-else>
      <!-- Bloc 1 — Santé opérationnelle -->
      <div class="rel-section">
        <h4 class="rel-section-title">
          <v-icon small color="#6B7280">mdi-heart-pulse</v-icon>
          {{ $t('org-dashboard.reliability-health') }}
        </h4>

        <div class="rel-health-grid">
          <!-- Uptime — gros chiffre -->
          <div class="rel-uptime-card" :class="uptimeColorClass">
            <div class="rel-uptime-value">{{ uptimeDisplay }}<span class="rel-uptime-unit">%</span></div>
            <div class="rel-uptime-label">{{ $t('org-dashboard.reliability-uptime') }}</div>
            <div class="rel-uptime-bar">
              <div class="rel-uptime-bar-fill" :class="uptimeColorClass" :style="{ width: uptimeBarWidth }"></div>
            </div>
          </div>

          <!-- KPIs secondaires -->
          <div class="rel-health-kpis">
            <div class="rel-kpi-card rel-kpi-card--warn">
              <div class="rel-kpi-icon">
                <v-icon size="18" color="#EF4444">mdi-power-plug-off</v-icon>
              </div>
              <div class="rel-kpi-content">
                <span class="rel-kpi-value">{{ offlineDisplay }}</span>
                <span class="rel-kpi-label">{{ $t('org-dashboard.reliability-offline') }}</span>
              </div>
            </div>
            <div class="rel-kpi-card">
              <div class="rel-kpi-icon">
                <v-icon size="18" color="#F59E0B">mdi-clock-alert-outline</v-icon>
              </div>
              <div class="rel-kpi-content">
                <span class="rel-kpi-value">{{ avgDowntimeDisplay }}</span>
                <span class="rel-kpi-label">{{ $t('org-dashboard.reliability-avg-downtime') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Bloc 2 — Performance de résolution -->
      <div v-if="resolution" class="rel-section">
        <h4 class="rel-section-title">
          <v-icon small color="#6B7280">mdi-wrench-clock</v-icon>
          {{ $t('org-dashboard.reliability-resolution') }}
        </h4>

        <div class="rel-resolution-grid">
          <!-- Résolu en < 24h -->
          <div class="rel-res-card rel-res-card--hero" :class="resolutionHeroClass">
            <div class="rel-res-hero-value">{{ resolved24hDisplay }}<span class="rel-res-hero-unit">%</span></div>
            <div class="rel-res-hero-label">{{ $t('org-dashboard.reliability-resolved-24h') }}</div>
            <div class="rel-res-hero-bar">
              <div class="rel-res-hero-bar-fill" :class="resolutionHeroClass" :style="{ width: resolved24hBarWidth }"></div>
            </div>
          </div>

          <!-- Temps moyen + Total résolus -->
          <div class="rel-res-kpis">
            <div class="rel-kpi-card">
              <div class="rel-kpi-icon">
                <v-icon size="18" color="#3B82F6">mdi-timer-outline</v-icon>
              </div>
              <div class="rel-kpi-content">
                <span class="rel-kpi-value">{{ avgResolutionDisplay }}</span>
                <span class="rel-kpi-label">{{ $t('org-dashboard.reliability-avg-resolution') }}</span>
              </div>
            </div>
            <div class="rel-kpi-card">
              <div class="rel-kpi-icon">
                <v-icon size="18" color="#22C55E">mdi-check-circle-outline</v-icon>
              </div>
              <div class="rel-kpi-content">
                <span class="rel-kpi-value">{{ totalResolvedDisplay }}</span>
                <span class="rel-kpi-label">{{ $t('org-dashboard.reliability-total-resolved') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'DashboardReliability',

  props: {
    data: { type: Object, default: null },
    loading: { type: Boolean, default: false },
  },

  computed: {
    // — Santé opérationnelle —
    uptimePercent() {
      return this.data?.uptimePercent ?? null
    },

    uptimeDisplay() {
      if (this.uptimePercent == null) return '--'
      return this.uptimePercent.toFixed(1)
    },

    uptimeBarWidth() {
      if (this.uptimePercent == null) return '0%'
      return Math.min(100, Math.max(0, this.uptimePercent)) + '%'
    },

    uptimeColorClass() {
      if (this.uptimePercent == null) return ''
      if (this.uptimePercent > 95) return 'rel--green'
      if (this.uptimePercent > 90) return 'rel--orange'
      return 'rel--red'
    },

    offlineDisplay() {
      const val = this.data?.currentlyOffline
      if (val == null) return '--'
      return val
    },

    avgDowntimeDisplay() {
      const hours = this.data?.avgCurrentDowntimeHours
      if (hours == null) return '--'
      if (hours >= 48) return Math.round(hours / 24) + 'j'
      return Math.round(hours) + 'h'
    },

    // — Performance de résolution —
    resolution() {
      return this.data?.resolution || null
    },

    resolved24hDisplay() {
      const val = this.resolution?.resolvedWithin24hPercent
      if (val == null) return '--'
      return Math.round(val)
    },

    resolved24hBarWidth() {
      const val = this.resolution?.resolvedWithin24hPercent
      if (val == null) return '0%'
      return Math.min(100, Math.max(0, val)) + '%'
    },

    resolutionHeroClass() {
      const val = this.resolution?.resolvedWithin24hPercent
      if (val == null) return ''
      if (val >= 80) return 'rel--green'
      if (val >= 50) return 'rel--orange'
      return 'rel--red'
    },

    avgResolutionDisplay() {
      const hours = this.resolution?.avgResolutionHours
      if (hours == null) return '--'
      if (hours >= 48) return Math.round(hours / 24) + 'j'
      return Math.round(hours) + 'h'
    },

    totalResolvedDisplay() {
      const val = this.resolution?.totalResolved
      if (val == null) return '--'
      return val
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-reliability {
  background: white;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.rel-header {
  margin-bottom: 20px;
}

.rel-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
}

.rel-subtitle {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 4px;
}

.rel-loading,
.rel-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 8px;

  p {
    font-size: 13px;
    color: #9ca3af;
    margin: 0;
  }
}

/* Sections */
.rel-section {
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
}

.rel-section-title {
  font-size: 12px;
  font-weight: 700;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

/* Color modifiers */
.rel--green {
  --rel-color: #059669;
  --rel-bg: #ecfdf5;
  --rel-border: #a7f3d0;
}

.rel--orange {
  --rel-color: #D97706;
  --rel-bg: #fffbeb;
  --rel-border: #fde68a;
}

.rel--red {
  --rel-color: #DC2626;
  --rel-bg: #fef2f2;
  --rel-border: #fecaca;
}

/* Bloc 1 — Santé opérationnelle */
.rel-health-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.rel-uptime-card {
  background: var(--rel-bg, #f9fafb);
  border: 1px solid var(--rel-border, #f3f4f6);
  border-radius: 12px;
  padding: 24px;
  text-align: center;
}

.rel-uptime-value {
  font-size: 48px;
  font-weight: 800;
  color: var(--rel-color, #1f2937);
  line-height: 1;
}

.rel-uptime-unit {
  font-size: 24px;
  font-weight: 600;
  opacity: 0.7;
}

.rel-uptime-label {
  font-size: 11px;
  color: #6b7280;
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.rel-uptime-bar {
  height: 6px;
  background: #f3f4f6;
  border-radius: 3px;
  margin-top: 12px;
  overflow: hidden;
}

.rel-uptime-bar-fill {
  height: 100%;
  border-radius: 3px;
  background: var(--rel-color, #9ca3af);
  transition: width 0.6s ease;
}

.rel-health-kpis {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* KPI cards */
.rel-kpi-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f9fafb;
  border-radius: 10px;
  padding: 16px;
  border: 1px solid #f3f4f6;
  flex: 1;

  &--warn {
    border-color: #fecaca;
    background: #fef2f2;
  }
}

.rel-kpi-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.rel-kpi-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.rel-kpi-value {
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.2;
}

.rel-kpi-label {
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
}

/* Bloc 2 — Performance de résolution */
.rel-resolution-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.rel-res-card--hero {
  background: var(--rel-bg, #f9fafb);
  border: 1px solid var(--rel-border, #f3f4f6);
  border-radius: 12px;
  padding: 24px;
  text-align: center;
}

.rel-res-hero-value {
  font-size: 48px;
  font-weight: 800;
  color: var(--rel-color, #1f2937);
  line-height: 1;
}

.rel-res-hero-unit {
  font-size: 24px;
  font-weight: 600;
  opacity: 0.7;
}

.rel-res-hero-label {
  font-size: 11px;
  color: #6b7280;
  margin-top: 4px;
}

.rel-res-hero-bar {
  height: 6px;
  background: #f3f4f6;
  border-radius: 3px;
  margin-top: 12px;
  overflow: hidden;
}

.rel-res-hero-bar-fill {
  height: 100%;
  border-radius: 3px;
  background: var(--rel-color, #9ca3af);
  transition: width 0.6s ease;
}

.rel-res-kpis {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
</style>
